import tokenRefreshManager from '../../utils/auth/tokenRefreshManager';
import logger from '../../utils/logging/logger';

// Manages the CSRF token lifecycle for HttpClient: fetching it (bound to the
// current auth-token's user id, with in-flight dedup, refresh-on-expiry, and
// retry/backoff) and clearing the cache on logout / user change.
//
// Extracted from HttpClient; the client exposes fetchCSRFToken()/clearCSRFToken()
// as thin delegators to preserve the public API used across the app.
export default class CsrfTokenManager {
  constructor({ baseURL, getTimeout }) {
    this.baseURL = baseURL;
    this.getTimeout = getTimeout;
    // CSRF token cache
    this.csrfToken = null;
    this.csrfTokenExpiresAt = null;
    this.csrfTokenUserId = null;
    this.csrfTokenPromise = null; // in-flight fetch dedup
  }

  getUserIdFromToken(token) {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      // Proper base64url decoding (JWT uses URL-safe base64)
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      
      // Backend uses 'userId' field (see server/routes/auth.js:84)
      return payload.userId || null;
    } catch (error) {
      logger.warn('Error decoding token to get user ID', { error });
      return null;
    }
  }

  async fetch(retryCount = 0) {
    // Prevent infinite recursion (max 2 retries)
    if (retryCount > 2) {
      logger.error('CSRF token fetch exceeded retry limit', { retryCount });
      return null;
    }

    // If already fetching, wait for that promise
    if (this.csrfTokenPromise) {
      return this.csrfTokenPromise;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('faithinsight_token');
    if (!token) {
      return null;
    }

    // Get current user ID from token
    const currentUserId = this.getUserIdFromToken(token);
    
    // Handle invalid token
    if (currentUserId === null) {
      logger.warn('Invalid token, clearing CSRF token');
      this.csrfToken = null;
      this.csrfTokenExpiresAt = null;
      this.csrfTokenUserId = null;
      return null;
    }
    
    // If cached token exists, check if user ID matches
    if (this.csrfToken && this.csrfTokenExpiresAt && Date.now() < this.csrfTokenExpiresAt) {
      // If user ID changed, clear old CSRF token
      if (this.csrfTokenUserId !== null && this.csrfTokenUserId !== currentUserId) {
        logger.debug('User ID changed, clearing old CSRF token', { oldUserId: this.csrfTokenUserId, newUserId: currentUserId });
        this.csrfToken = null;
        this.csrfTokenExpiresAt = null;
        this.csrfTokenUserId = null;
      } else if (this.csrfTokenUserId === currentUserId) {
        // User ID matches, return cached token
        return this.csrfToken;
      }
    }

    // Fetch new CSRF token
    this.csrfTokenPromise = (async () => {
      try {
        // Use adaptive timeout for CSRF token fetch (same as API requests)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
        }, this.getTimeout());
        
        const response = await fetch(`${this.baseURL}/csrf-token`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          // If 401, check if it's a token expiration issue
          if (response.status === 401) {
            // Try to parse error to see if it's token expiration
            let errorData = null;
            try {
              errorData = await response.json();
            } catch (e) {
              // Not JSON, treat as generic 401
            }
            
            // Check if this is a token expiration (not just missing auth)
            const isTokenExpired = errorData && (
              errorData.error === 'Token expired' || 
              errorData.error === 'Invalid token' ||
              errorData.message?.includes('expired') ||
              errorData.message?.includes('Token expired') ||
              errorData.message?.includes('Invalid token')
            );
            
            // If token is expired, wait for refresh and retry
            if (isTokenExpired) {
              try {
                // Check if refresh is already in progress
                if (tokenRefreshManager.isRefreshInProgress()) {
                  logger.debug('[CSRF Token] Token refresh in progress, waiting...');
                  await tokenRefreshManager.waitForRefresh();
                  // Retry CSRF token fetch with new token
                  this.csrfTokenPromise = null;
                  return this.fetch(retryCount + 1);
                } else {
                  // Perform refresh with locking
                  logger.debug('[CSRF Token] Token expired, refreshing...');
                  await tokenRefreshManager.refreshTokenWithLock(this.baseURL);
                  // Retry CSRF token fetch with new token
                  this.csrfTokenPromise = null;
                  return this.fetch(retryCount + 1);
                }
              } catch (refreshError) {
                // Refresh failed, clear tokens and return null
                logger.error('[CSRF Token] Token refresh failed', { refreshError });
                this.csrfToken = null;
                this.csrfTokenExpiresAt = null;
                this.csrfTokenUserId = null;
                return null;
              }
            } else {
              // Not a token expiration, just clear and return null
              this.csrfToken = null;
              this.csrfTokenExpiresAt = null;
              this.csrfTokenUserId = null;
              return null;
            }
          }
          
          // Retry on 500/503 errors (server errors)
          if ((response.status === 500 || response.status === 503) && retryCount < 2) {
            logger.debug(`CSRF Token server error ${response.status}, retrying`, { attempt: retryCount + 1, maxAttempts: 2 });
            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
            this.csrfTokenPromise = null;
            return this.fetch(retryCount + 1);
          }
          
          throw new Error(`Failed to fetch CSRF token: ${response.status}`);
        }

        const data = await response.json();
        if (data.success && data.csrfToken) {
          // Verify user ID still matches (token might have refreshed during fetch)
          const verifyUserId = this.getUserIdFromToken(localStorage.getItem('faithinsight_token'));
          if (verifyUserId !== null && verifyUserId !== currentUserId) {
            logger.debug('User ID changed during CSRF fetch, clearing token and retrying', { oldUserId: currentUserId, newUserId: verifyUserId });
            this.csrfToken = null;
            this.csrfTokenExpiresAt = null;
            this.csrfTokenUserId = null;
            // Retry fetch with new user ID (with retry counter to prevent infinite loops)
            this.csrfTokenPromise = null;
            return this.fetch(retryCount + 1);
          }
          
          this.csrfToken = data.csrfToken;
          // Set expiry to 23 hours (slightly less than server's 24 hours)
          this.csrfTokenExpiresAt = Date.now() + (23 * 60 * 60 * 1000);
          // Store user ID associated with this CSRF token
          this.csrfTokenUserId = currentUserId;
          return this.csrfToken;
        }

        return null;
      } catch (error) {
        // Handle timeout and network errors with retry
        const isNetworkError = error.name === 'AbortError' || 
                              error.name === 'TypeError' || 
                              error.message?.includes('fetch') ||
                              error.message?.includes('timeout');
        
        if (isNetworkError && retryCount < 2) {
          logger.debug(`CSRF Token network error, retrying`, { attempt: retryCount + 1, maxAttempts: 2, error: error.message });
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
          this.csrfTokenPromise = null;
          return this.fetch(retryCount + 1);
        }

        logger.error('Error fetching CSRF token', { error, retryCount });
        return null;
      } finally {
        this.csrfTokenPromise = null;
      }
    })();

    return this.csrfTokenPromise;
  }

  clear() {
    this.csrfToken = null;
    this.csrfTokenExpiresAt = null;
    this.csrfTokenUserId = null;
    this.csrfTokenPromise = null;
  }
}
